import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './NewsCard.css';

const fallbackImage = `${process.env.PUBLIC_URL || ''}/assets/banner.png`;

const NewsCard = ({ article }) => {
  const navigate = useNavigate();
  const [hoverRating, setHoverRating] = useState(0);
  const [userRated, setUserRated] = useState(false);

  const publishedDate = useMemo(() => {
    if (!article.created_at) {
      return '';
    }
    return new Date(article.created_at).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }, [article?.created_at]);

  if (!article) {
    return null;
  }

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  };

  const articleSlug = article.slug || generateSlug(article.title);

  const handleRating = (value) => {
    setUserRated(true);
    // Here you can add API call to save the rating
    setTimeout(() => {
      setUserRated(false);
    }, 2000);
  };

  const handleReadMore = () => {
    navigate(`/news/${articleSlug}`);
  };

  const buildExcerpt = (content) => {
    if (!content) {
      return 'Read this article for practical driving knowledge.';
    }
    const plain = content
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return plain.length > 120 ? `${plain.slice(0, 117)}...` : plain;
  };

  // Calculate average rating from article data if available
  const averageRating = article.average_rating || 0;
  const ratingCount = article.rating_count || 0;

  return (
    <article className="news-card">
      <div className="news-card-image-wrapper">
        <img
          className="news-card-image"
          src={article.img || fallbackImage}
          alt={article.title}
          loading="lazy"
        />
        {article.category_name && (
          <span className="news-card-category">{article.category_name}</span>
        )}
      </div>

      <div className="news-card-content">
        <h3 className="news-card-title">{article.title}</h3>

        <div className="news-card-meta">
          <span className="news-card-date">{publishedDate}</span>
          {article.views && (
            <span className="news-card-views">{Number(article.views).toLocaleString()} views</span>
          )}
        </div>

        <p className="news-card-excerpt">{buildExcerpt(article.content)}</p>

        <div className="news-card-rating-section">
          <div className="news-card-rating-display">
            {averageRating > 0 && (
              <>
                <div className="news-card-stars-display">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span
                      key={star}
                      className={`news-card-star ${star <= Math.round(averageRating) ? 'filled' : ''}`}
                    >
                      ★
                    </span>
                  ))}
                </div>
                <span className="news-card-rating-text">
                  {averageRating.toFixed(1)} ({ratingCount} {ratingCount === 1 ? 'rating' : 'ratings'})
                </span>
              </>
            )}
          </div>

          <div className="news-card-rating-interaction">
            {!userRated ? (
              <div className="news-card-stars-interactive">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    className={`news-card-star-btn ${star <= hoverRating ? 'hover' : ''}`}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => handleRating(star)}
                    aria-label={`Rate ${star} stars`}
                    title="Rate this article"
                  >
                    ★
                  </button>
                ))}
              </div>
            ) : (
              <span className="news-card-rating-feedback">Rating saved ✓</span>
            )}
          </div>
        </div>

        <button className="news-card-read-btn" onClick={handleReadMore}>
          Read Full Article
        </button>
      </div>
    </article>
  );
};

export default NewsCard;
